import { forwardRef, ReactNode, useEffect, useRef } from 'react'
import cx from 'classnames'
import { CommandPaletteGroup, CommandPaletteItem } from './types'
import styles from './CommandPaletteResults.module.css'

interface CommandPaletteResultsProps {
  groups: CommandPaletteGroup[]
  activeIndex: number
  onItemActivate: (flatIndex: number) => void
  onItemSelect: (item: CommandPaletteItem) => void
  query?: string
}

export function highlightMatch(text: string, query: string): ReactNode[] {
  if (!query) return [text]

  const lowerText = text.toLowerCase()
  const lowerQuery = query.toLowerCase()
  const matchIndex = lowerText.indexOf(lowerQuery)

  if (matchIndex === -1) return [text]

  const before = text.slice(0, matchIndex)
  const match = text.slice(matchIndex, matchIndex + query.length)
  const after = text.slice(matchIndex + query.length)

  return [
    before,
    <mark key="match">{match}</mark>,
    after,
  ]
}

export function CommandPaletteResults({
  groups,
  activeIndex,
  onItemActivate,
  onItemSelect,
  query,
}: CommandPaletteResultsProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const activeItemRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (activeItemRef.current) {
      activeItemRef.current.scrollIntoView({ block: 'nearest' })
    }
  }, [activeIndex])

  let flatIndex = 0

  return (
    <div
      className={styles.CommandPaletteResults}
      ref={containerRef}
      role="listbox"
      aria-label="Search results"
    >
      {groups.map((group) => {
        if (group.items.length === 0) return null

        return (
          <div key={group.id} role="group" aria-labelledby={`group-${group.id}`}>
            <div
              className={styles.CommandPaletteGroup__header}
              id={`group-${group.id}`}
            >
              {group.label}
            </div>
            {group.items.map((item) => {
              const currentFlatIndex = flatIndex++
              const isActive = currentFlatIndex === activeIndex

              return (
                <CommandPaletteResultItem
                  key={item.id}
                  item={item}
                  isActive={isActive}
                  flatIndex={currentFlatIndex}
                  query={query}
                  onActivate={onItemActivate}
                  onSelect={onItemSelect}
                  ref={isActive ? activeItemRef : undefined}
                />
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

interface CommandPaletteResultItemProps {
  item: CommandPaletteItem
  isActive: boolean
  flatIndex: number
  query?: string
  onActivate: (flatIndex: number) => void
  onSelect: (item: CommandPaletteItem) => void
}

const CommandPaletteResultItem = forwardRef<HTMLDivElement, CommandPaletteResultItemProps>(
  function CommandPaletteResultItem({
    item,
    isActive,
    flatIndex,
    query,
    onActivate,
    onSelect,
  }, ref) {
  return (
    <div
      ref={ref}
      className={cx(styles.CommandPaletteItem, {
        [styles.isActive]: isActive,
      })}
      role="option"
      aria-selected={isActive}
      onMouseEnter={() => onActivate(flatIndex)}
      onClick={() => onSelect(item)}
    >
      {item.icon && (
        <span className={styles.CommandPaletteItem__icon} aria-hidden="true">
          {item.icon}
        </span>
      )}
      <div className={styles.CommandPaletteItem__text}>
        <div className={styles.CommandPaletteItem__label}>
          {query ? highlightMatch(item.label, query) : item.label}
        </div>
        {item.subtitle && (
          <div className={styles.CommandPaletteItem__subtitle}>
            {query ? highlightMatch(item.subtitle, query) : item.subtitle}
          </div>
        )}
      </div>
      {item.kbd && (
        <span className={styles.CommandPaletteItem__right}>
          {item.kbd}
        </span>
      )}
      <span className={styles.CommandPaletteItem__enter} aria-hidden="true">
        &#x21B5;
      </span>
    </div>
  )
})
